import {
  ActionButton,
  Banner,
  ColumnDef,
  ColumnVisibilityModel,
  ControlledTextField,
  DataGridPage,
  DataGridPageQueryParams,
  SelectionModel,
  SortModel,
  useConfirmDialog,
  useSnackbar,
} from '@if/mui'
import { Stack } from '@mui/material'
import { useClearFailedVendors, useForceTransferVendors, useGetFailedVendors } from '@src/api/failed-vendors-api'
import { useCompany } from '@src/services/company-hooks'
import { FailedVendorSyncItem } from '@src/types/failed-vendor-sync'
import { RefreshCw } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FailedVendorsFiltersType, failedVendorsFiltersSchema } from './failed-vendors-filters-schema'
import { useForceTransferVendorsDialog } from './ForceTransferVendorsDialog-hook'

const DEFAULT_SORT: SortModel<FailedVendorSyncItem> = { name: 'lastFailedAt', direction: 'desc' }
const DEFAULT_LIMIT = 25

const useCompanyId = () => {
  const selectedCompany = useCompany()
  return selectedCompany ? String(selectedCompany) : ''
}

const matchesFilters = (row: FailedVendorSyncItem, filters: FailedVendorsFiltersType) => {
  const search = filters.search?.trim().toLowerCase()
  if (search && ![row.merchantId, row.merchantName].some((value) => value?.toLowerCase().includes(search))) {
    return false
  }

  const errorContains = filters.errorContains?.trim().toLowerCase()
  if (errorContains && !row.errorReason.toLowerCase().includes(errorContains)) {
    return false
  }

  return !filters.minAttempts || row.attemptCount >= filters.minAttempts
}

/**
 * The endpoint hands back every failed merchant of the company in one call, so the filters,
 * the sort and the page are applied here instead of being sent to the server.
 */
const useFailedVendorsData = ({
  filters,
  pagination,
  sortModel,
}: DataGridPageQueryParams<FailedVendorSyncItem, FailedVendorsFiltersType>) => {
  const companyId = useCompanyId()
  const { data: vendors, isLoading } = useGetFailedVendors(companyId)

  const data = useMemo(() => {
    const rows = (vendors ?? []).filter((row) => matchesFilters(row, filters))

    if (sortModel) {
      rows.sort((first, second) => {
        const left = first[sortModel.name] ?? ''
        const right = second[sortModel.name] ?? ''
        const comparison = left < right ? -1 : left > right ? 1 : 0
        return sortModel.direction === 'asc' ? comparison : -comparison
      })
    }

    return rows.slice(pagination.offset, pagination.offset + pagination.limit)
  }, [vendors, filters, sortModel, pagination])

  return { data, isLoading }
}

const FailedVendorsList = () => {
  const { t } = useTranslation()
  const companyId = useCompanyId()
  const { showSnackbar, showSuccess } = useSnackbar()
  const [retryingMerchantId, setRetryingMerchantId] = useState<string | null>(null)
  const [selectionModel, setSelectionModel] = useState<SelectionModel>([])
  const [visibilityModel, setVisibilityModel] = useState<ColumnVisibilityModel>({})

  const { data: vendors, error } = useGetFailedVendors(companyId)
  const [forceTransferVendors] = useForceTransferVendors(companyId)
  const [clearFailedVendors] = useClearFailedVendors(companyId)
  const forceTransferDialog = useForceTransferVendorsDialog(companyId)

  const confirmClear = useConfirmDialog({
    mode: 'destructive',
    title: t('admin.transactionRetry.failedVendors.confirmClear.title'),
    message: t('admin.transactionRetry.failedVendors.confirmClear.message'),
    onConfirm: () => clearFailedVendors(),
  })

  const handleClearList = useCallback(async () => {
    const cleared = await confirmClear.open()
    if (cleared) {
      showSuccess(t('admin.transactionRetry.failedVendors.messages.listCleared'))
    }
  }, [confirmClear, showSuccess, t])

  const handleRetry = useCallback(
    async (merchantId: string) => {
      setRetryingMerchantId(merchantId)
      try {
        const [result] = await forceTransferVendors([merchantId])
        if (result.success) {
          showSuccess(t('admin.transactionRetry.failedVendors.messages.retrySuccess', { merchantId }))
          return
        }
        showSnackbar(
          t('admin.transactionRetry.failedVendors.messages.retryFailed', {
            merchantId,
            error: result.errorReason ?? t('admin.transactionRetry.errors.unknown'),
          }),
          { severity: 'error' },
        )
      } catch {
        showSnackbar(
          t('admin.transactionRetry.failedVendors.messages.retryFailed', {
            merchantId,
            error: t('admin.transactionRetry.errors.unknown'),
          }),
          { severity: 'error' },
        )
      } finally {
        setRetryingMerchantId(null)
      }
    },
    [forceTransferVendors, showSnackbar, showSuccess, t],
  )

  const handleRetrySelected = useCallback(async () => {
    const merchantIds = selectionModel.map(String)
    const results = await forceTransferVendors(merchantIds)
    const failures = results.filter((result) => !result.success).map((result) => result.merchantId)
    const transferred = results.length - failures.length

    setSelectionModel([])
    if (failures.length === 0) {
      showSuccess(t('admin.transactionRetry.forceTransfer.messages.success', { transferred }))
      return
    }
    showSnackbar(
      t('admin.transactionRetry.forceTransfer.messages.partial', {
        transferred,
        failed: failures.length,
        merchants: failures.join(', '),
      }),
      { severity: transferred > 0 ? 'warning' : 'error' },
    )
  }, [selectionModel, forceTransferVendors, showSnackbar, showSuccess, t])

  const columns = useMemo<ColumnDef<FailedVendorSyncItem>[]>(
    () => [
      {
        name: 'merchantId',
        type: 'reference',
        header: t('admin.transactionRetry.failedVendors.columns.merchantId'),
        sorted: true,
        hideable: false,
        minWidth: 150,
      },
      {
        name: 'merchantName',
        type: 'title',
        header: t('admin.transactionRetry.failedVendors.columns.name'),
        sorted: true,
        minWidth: 200,
      },
      {
        name: 'errorReason',
        type: 'string',
        header: t('admin.transactionRetry.failedVendors.columns.error'),
        minWidth: 280,
      },
      {
        name: 'attemptCount',
        type: 'number',
        header: t('admin.transactionRetry.failedVendors.columns.attempts'),
        sorted: true,
        align: 'center',
        width: 110,
      },
      {
        name: 'lastFailedAt',
        type: 'date',
        header: t('admin.transactionRetry.failedVendors.columns.lastFailedAt'),
        sorted: true,
        width: 180,
      },
      {
        name: 'actions',
        type: 'action',
        actions: [
          {
            icon: RefreshCw,
            label: t('admin.transactionRetry.failedVendors.actions.retry'),
            disabled: (row) => retryingMerchantId === row.merchantId,
            onClick: (row) => void handleRetry(row.merchantId),
          },
        ],
      },
    ],
    [handleRetry, retryingMerchantId, t],
  )

  return (
    <Stack spacing={2}>
      {error && (
        <Banner severity="error">
          {error instanceof Error ? error.message : t('admin.transactionRetry.errors.unknown')}
        </Banner>
      )}

      <DataGridPage
        schema={failedVendorsFiltersSchema}
        useData={useFailedVendorsData}
        columns={columns}
        dataSourceIdKey="merchantId"
        defaultSort={DEFAULT_SORT}
        defaultLimit={DEFAULT_LIMIT}
        searchPlaceholder={t('admin.transactionRetry.failedVendors.filters.searchPlaceholder')}
        dock
        filterForm={{
          content: ({ form }) => (
            <Stack spacing={2} sx={{ pt: 1, minWidth: 320 }}>
              <ControlledTextField
                control={form.control}
                name="errorContains"
                label={t('admin.transactionRetry.failedVendors.filters.errorContains')}
              />
              <ControlledTextField
                control={form.control}
                name="minAttempts"
                type="number"
                label={t('admin.transactionRetry.failedVendors.filters.minAttempts')}
              />
            </Stack>
          ),
          renderChip: {
            errorContains: (value) => t('admin.transactionRetry.failedVendors.filters.chips.errorContains', { value }),
            minAttempts: (value) => t('admin.transactionRetry.failedVendors.filters.chips.minAttempts', { value }),
          },
        }}
        selectable="multi"
        selectionModel={selectionModel}
        onSelectionModelChange={setSelectionModel}
        columnControl
        visibilityModel={visibilityModel}
        onVisibilityModelChange={setVisibilityModel}
        canChangePageSize
        toolbar={
          selectionModel.length > 0 && (
            <ActionButton emphasis="principal" onClick={() => void handleRetrySelected()}>
              {t('admin.transactionRetry.failedVendors.actions.retrySelected', { selected: selectionModel.length })}
            </ActionButton>
          )
        }
        actions={
          <>
            <ActionButton emphasis="principal" onClick={() => void forceTransferDialog.create()}>
              {t('admin.transactionRetry.forceTransfer.sectionTitle')}
            </ActionButton>

            {/* Clears the whole company list, filters or not, so the guard is the unfiltered count. */}
            <ActionButton
              tone="destructive"
              onClick={() => void handleClearList()}
              disabled={(vendors ?? []).length === 0}
            >
              {t('admin.transactionRetry.failedVendors.actions.clearList')}
            </ActionButton>
          </>
        }
      />
    </Stack>
  )
}

export default FailedVendorsList
